import { Injectable, OnDestroy } from '@angular/core';
import { BrowserNotificationsService } from './bowser-notification.service';
import { WindowRef } from 'src/sb-tools/typescript/services/window-ref.service';
import { TranslateService } from '@ngx-translate/core';
import { TicketDetailsPanelService } from './ticket-details-panel.service';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { take } from 'rxjs/operators';
import { TicketAssignedEventModel } from '../../core/models/dto/signalR/ticket-assigned-event-model.model';
import { NotifyAboutNewReplyEventModel } from '../../core/models/dto/signalR/notify-about-new-reply-event-model.model';
import { TicketAddedEventModel } from '../../core/models/dto/signalR/ticket-added-event-model.model';
import { NotificationDTO } from '../../core/models/dto/notification.dto';

@Injectable({
    providedIn: 'root',
})
export class TicketNotificationsService implements OnDestroy {
    private unsubscriber = new Subject<void>();

    constructor(
        private browserNotificationsService: BrowserNotificationsService,
        private windowRef: WindowRef,
        private translateService: TranslateService,
        private ticketDetailsPanelService: TicketDetailsPanelService,
        private commonDataService: CommonDataService,
    ) {}

    showAssignNotification(notificationModel: TicketAssignedEventModel): void {
        if (!('Notification' in window)) {
            return;
        }
        if (this.windowRef.nativeWindow['Notification'].permission === 'default') {
            this.browserNotificationsService
                .requestPermission()
                .pipe(takeUntil(this.unsubscriber), take(1))
                .subscribe((permission) => {
                    if (permission === 'granted') {
                        this.processAssignNotification(notificationModel);
                    }
                });
        } else if (this.windowRef.nativeWindow['Notification'].permission === 'granted') {
            this.processAssignNotification(notificationModel);
        }
    }

    showNewMessageNotification(notificationModel: NotifyAboutNewReplyEventModel, isWindowFocused: boolean): void {
        if (!('Notification' in window)) {
            return;
        }
        if (this.windowRef.nativeWindow['Notification'].permission === 'default') {
            this.browserNotificationsService
                .requestPermission()
                .pipe(takeUntil(this.unsubscriber), take(1))
                .subscribe((permission) => {
                    if (permission === 'granted') {
                        this.processNewMessageNotification(notificationModel, isWindowFocused);
                    }
                });
        } else if (this.windowRef.nativeWindow['Notification'].permission === 'granted') {
            this.processNewMessageNotification(notificationModel, isWindowFocused);
        }
    }

    showNewTicketNotification(notificationModel: TicketAddedEventModel): void {
        if (!('Notification' in window)) {
            return;
        }
        if (this.windowRef.nativeWindow['Notification'].permission === 'default') {
            this.browserNotificationsService
                .requestPermission()
                .pipe(takeUntil(this.unsubscriber), take(1))
                .subscribe((permission) => {
                    if (permission === 'granted') {
                        this.processNewTicketNotification(notificationModel);
                    }
                });
        } else if (this.windowRef.nativeWindow['Notification'].permission === 'granted') {
            this.processNewTicketNotification(notificationModel);
        }
    }

    showSurveyNotification(notificationModel: NotificationDTO): void {
        if (!('Notification' in window)) {
            return;
        }
        if (this.windowRef.nativeWindow['Notification'].permission === 'default') {
            this.browserNotificationsService
                .requestPermission()
                .pipe(takeUntil(this.unsubscriber), take(1))
                .subscribe((permission) => {
                    if (permission === 'granted') {
                        this.processSurveyNotification(notificationModel);
                    }
                });
        } else if (this.windowRef.nativeWindow['Notification'].permission === 'granted') {
            this.processSurveyNotification(notificationModel);
        }
    }

    private processAssignNotification(notificationModel: TicketAssignedEventModel): void {
        this.browserNotificationsService
            .generateNotification({
                title: this.translateService.instant('New assigned ticket!'),
                alertContent:
                    notificationModel.assigner.name ||
                    notificationModel.assigner.email +
                        ' ' +
                        this.translateService.instant('assigned a ticket to you') +
                        '\n #' +
                        notificationModel.ticketId,
            })
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                if (data.event.type === 'click') {
                    this.openTicketFromNotification(notificationModel.ticketId, notificationModel.communityName);
                }
            });
        this.playNotificationSound();
    }

    private processNewMessageNotification(
        notificationModel: NotifyAboutNewReplyEventModel,
        isWindowFocused: boolean,
    ): void {
        const newChatMessage = notificationModel.reply;

        if (this.ticketDetailsPanelService.currentTicketId === newChatMessage.ticketId && isWindowFocused) {
            return;
        }
        this.browserNotificationsService
            .generateNotification({
                title: this.translateService.instant('New reply'),
                alertContent: newChatMessage.message.text,
            })
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                if (data.event.type === 'click') {
                    this.openTicketFromNotification(newChatMessage.ticketId, notificationModel.communityName);
                }
            });
        this.playNotificationSound();
    }

    private processSurveyNotification(notificationModel: NotificationDTO): void {
        this.browserNotificationsService
            .generateNotification({
                title: this.translateService.instant('New survey response'),
                alertContent:
                    this.translateService.instant('Client added a review to ticket') +
                    '\n #' +
                    notificationModel.ticketId,
            })
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                if (data.event.type === 'click') {
                    this.openTicketFromNotification(notificationModel.ticketId, notificationModel.communityName);
                }
            });
        this.playNotificationSound();
    }

    private processNewTicketNotification(notificationModel: TicketAddedEventModel): void {
        const newTicket = notificationModel.ticket;
        this.browserNotificationsService
            .generateNotification({
                title: this.translateService.instant('New ticket'),
                alertContent: newTicket.message.text,
            })
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                if (data.event.type === 'click') {
                    this.openTicketFromNotification(newTicket.id, notificationModel.communityName);
                }
            });
        this.playNotificationSound();
    }

    private openTicketFromNotification(ticketId: number, community: string): void {
        this.windowRef.nativeWindow.focus();

        if (community === this.commonDataService.community) {
            this.ticketDetailsPanelService.open(ticketId);
        } else {
            this.windowRef.nativeWindow.location.href =
                this.windowRef.nativeWindow.location.origin + '/' + community + '/tickets?ticketId=' + ticketId;
        }
    }

    private playNotificationSound(): void {
        const notificationSound = document.getElementById('notification-audio');
        notificationSound['play']();
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
